import { dayText, inRotation } from "./accounts";
import type { Account, CalendarValue, CycleStop, FillBand } from "./api";

/** Monday is 0, as the API counts the days of the week. */
export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const RULE_TEXT: Record<CalendarValue["holiday_rule"], string> = {
  next: "The next workday",
  previous: "The workday before",
};
export const NAME_MAX = 60;
export const MARGIN_MAX = 50;
export const RISK_MIN = 1;
export const RISK_MAX = 50;

/** The day of the week of an ISO date, Monday 0. */
export const weekdayOf = (iso: string) => (new Date(`${iso}T12:00:00Z`).getUTCDay() + 6) % 7;

export const shortDay = (iso: string) => `${WEEKDAYS[weekdayOf(iso)].slice(0, 3)} ${dayText(iso)}`;

export const percentText = (share: number) => `${Math.round(share * 100)}%`;

/** Rule U-22: a share at the visit over 100% shows as words, never as a percent over 100%. */
export const atVisitText = (share: number) => (share > 1 ? "full before" : percentText(share));

/** Rule D-15: the fill estimate at the visit decides when the account has one; else the D-14 load. */
export function visitShareText(share: number | null | undefined, estimate?: Pick<FillBand, "p50"> | null): string | null {
  if (estimate) return estimate.p50 > 1 ? "full before" : `about ${percentText(estimate.p50)}`;
  return share == null ? null : atVisitText(share);
}

/** Design 8.5: only an account in the rotation has a next visit and an early-warning pull. */
export const earlyPullOf = (account: Account) => inRotation(account) && Boolean(account.projection?.early_pull);

export function nextVisitText(account: Account): string {
  const visit = account.projection?.next_visit;
  if (!visit || !inRotation(account)) return "–";
  return shortDay(visit);
}

export function projectedText(account: Account): string {
  const share = account.projection?.projected_share;
  if (share == null || !account.projection?.next_visit || !inRotation(account)) return "–";
  return share > 1 ? "Full before the visit" : `${percentText(share)} full at the visit`;
}

/** The list cell: the next visit day and the share at that visit, as "Mon Nov 2 · 64%". */
/** The day and the fill at that visit; no fill with no visit. */
export function visitCellParts(account: Account): { day: string; share: string | null } {
  const day = nextVisitText(account);
  const share = visitShareText(account.projection?.projected_share, account.projection?.estimate?.at_visit);
  return { day, share: day === "–" ? null : share };
}

export function visitCellText(account: Account): string {
  const { day, share } = visitCellParts(account);
  return share == null ? day : `${day} · ${share}`;
}

type Pull = Pick<CycleStop, "next_visit" | "projected_share" | "overflow_risk">;

/** The chance of overflow as text; a chance under 1% shows as "under 1%". */
export const riskText = (risk: number) => (risk < 0.01 ? "under 1%" : percentText(risk));

/** The cycle board line under an early-pull chip. */
export function earlyPullVisitText(found: Pull): string {
  if (!found.next_visit) return "";
  const share =
    found.overflow_risk != null
      ? ` · ${riskText(found.overflow_risk)} chance of overflow`
      : found.projected_share == null
        ? ""
        : found.projected_share > 1
          ? " · full before the visit"
          : ` · ${percentText(found.projected_share)} at the visit`;
  return `Next visit ${shortDay(found.next_visit)}${share}`;
}

export function earlyPullTitle(found: Pull, margin: number, risk = 0.1): string {
  if (found.next_visit && found.overflow_risk != null)
    return `${riskText(found.overflow_risk)} chance of overflow by the next visit on ${shortDay(found.next_visit)}, ${percentText(risk)} or more`;
  const limit = `above ${Math.round((1 - margin) * 100)}%`;
  if (!found.next_visit || found.projected_share == null) return `Projected ${limit} at the next visit`;
  if (found.projected_share > 1) return `Projected full before the next visit on ${shortDay(found.next_visit)}, ${limit}`;
  return `Projected ${percentText(found.projected_share)} full at the next visit on ${shortDay(found.next_visit)}, ${limit}`;
}

export function weekDaysText(visitDay: string | undefined, holidays: { day: string; name: string }[]): string {
  const visit = visitDay ? `Visit day ${shortDay(visitDay)}` : "";
  const off = holidays.map((holiday) => `${holiday.name} on ${shortDay(holiday.day)}`).join(", ");
  return [visit, off && `Holiday: ${off}`].filter(Boolean).join(" · ");
}

export function nameLengthText(name: string): string | null {
  const length = name.trim().length;
  return length > NAME_MAX ? `A name is ${NAME_MAX} characters at most (now ${length}).` : null;
}

export type HolidayProblem = { field: "day" | "name"; text: string };

/** Read the holiday form; give the error and its field, or the clean holiday. */
export function holidayChange(day: string, name: string, taken: readonly string[]): HolidayProblem | { day: string; name: string } {
  const clean = name.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || Number.isNaN(Date.parse(`${day}T12:00:00Z`))) return { field: "day", text: "Choose a day." };
  const year = Number(day.slice(0, 4));
  if (year < 2000 || year > 2100) return { field: "day", text: "Choose a day from 2000 to 2100." };
  if (!clean) return { field: "name", text: "Give the holiday a name." };
  const long = nameLengthText(clean);
  if (long) return { field: "name", text: long };
  if (taken.includes(day)) return { field: "day", text: "A holiday is on this day already." };
  return { day, name: clean };
}

export interface CalendarForm {
  weekStarts: string;
  cycleStart: string;
  rule: CalendarValue["holiday_rule"];
  margin: string;
  risk: string;
}

export const calendarForm = (value: CalendarValue): CalendarForm => ({
  weekStarts: String(value.week_starts),
  cycleStart: value.cycle_start,
  rule: value.holiday_rule,
  margin: String(Math.round(value.early_margin * 1000) / 10),
  risk: String(Math.round(value.early_risk * 1000) / 10),
});

/** Read the calendar form; give the error and its field, or the value to save. */
export function calendarChange(form: CalendarForm): CalendarValue | { key: keyof CalendarForm; text: string } {
  const weekStarts = Number(form.weekStarts);
  if (!Number.isInteger(weekStarts) || weekStarts < 0 || weekStarts > 6) return { key: "weekStarts", text: "Choose the first day of a week." };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.cycleStart)) return { key: "cycleStart", text: "Choose the first day of cycle 1." };
  const year = Number(form.cycleStart.slice(0, 4));
  if (year < 2000 || year > 2100) return { key: "cycleStart", text: "Choose a cycle start from 2000 to 2100." };
  if (weekdayOf(form.cycleStart) !== weekStarts)
    return { key: "cycleStart", text: `The cycle start must be a ${WEEKDAYS[weekStarts]}, the first day of a week.` };
  const clean = form.margin.trim().replace(/\s*%$/, "");
  const margin = Number(clean);
  if (clean === "" || !Number.isFinite(margin) || margin < 0 || margin > MARGIN_MAX)
    return { key: "margin", text: `The margin must be from 0% to ${MARGIN_MAX}%.` };
  const cleanRisk = form.risk.trim().replace(/\s*%$/, "");
  const risk = Number(cleanRisk);
  if (cleanRisk === "" || !Number.isFinite(risk) || risk < RISK_MIN || risk > RISK_MAX)
    return { key: "risk", text: `The chance must be from ${RISK_MIN}% to ${RISK_MAX}%.` };
  return {
    week_starts: weekStarts,
    cycle_start: form.cycleStart,
    holiday_rule: form.rule,
    early_margin: Math.round(margin * 10) / 1000,
    early_risk: Math.round(risk * 10) / 1000,
  };
}
